import React, { useContext } from 'react'
import moment from 'moment'
import {
  Document,
  Image,
  Page,
  PDFViewer,
  Text,
  View,
} from '@react-pdf/renderer'
import { TenantSettingsContext } from '../../../../App'
import { vatMultiplierForItem } from '../../../../helpers/region'
import BigModal from '../../../../components/Modals/BigModal'
import Box from '@mui/material/Box'

const PDFInvoice = ({ open, onClose, invoice }) => {
  const tenantSettings = useContext(TenantSettingsContext)
  const orgSettings = tenantSettings.orgConfig?.orgSettings ?? {}
  const orgAddress = orgSettings.address ?? {}
  const currency = orgSettings?.currency ?? '£'
  const region = tenantSettings.tenantInfo?.region
  const billToName =
    invoice?.organisation?.orgName || invoice?.patientCustomer?.name || '—'
  const billToAddress = invoice?.organisation
    ? [
        invoice.organisation.orgAddressLineOne,
        invoice.organisation.orgAddressLineTwo,
        invoice.organisation.orgAddressCity,
        invoice.organisation.orgAddressPostCode,
      ]
    : [invoice?.patientCustomer?.address]

  const _subtotal = parseFloat(
    (invoice.items || []).reduce((accumulator, item) => {
      const { unitPrice, quantity } = item
      const total = unitPrice * quantity
      accumulator += total
      return accumulator
    }, 0),
  ).toFixed(2)

  const _vatTotal = parseFloat(
    (invoice.items || []).reduce((accumulator, item) => {
      const { unitPrice, quantity, vat } = item
      const rate = vatMultiplierForItem(vat, region) - 1
      return accumulator + unitPrice * quantity * rate
    }, 0),
  ).toFixed(2)

  // Credit notes reduce the balance by their VAT-inclusive value, matching
  // the server's netAmount maths
  const _creditedTotal = parseFloat(
    (invoice.creditNotes || []).reduce(
      (accumulator, note) =>
        accumulator +
        (note.items || []).reduce(
          (noteTotal, item) =>
            noteTotal +
            (Number(item.quantity) || 0) *
              (Number(item.unitPrice) || 0) *
              vatMultiplierForItem(item.vat, region),
          0,
        ),
      0,
    ),
  ).toFixed(2)

  const _paymentsTotal = parseFloat(
    (invoice.payments || []).reduce((accumulator, payment) => {
      accumulator += Number(payment.amount) || 0
      return accumulator
    }, 0),
  ).toFixed(2)

  const SmallText = ({ title, value }) => (
    <View style={{ flexDirection: 'row', gap: 2, fontSize: 12, width: '80%' }}>
      <Text
        style={{ fontFamily: 'Courier-Bold', width: '50%', textAlign: 'right' }}
      >
        {title}:
      </Text>
      <Text style={{ fontFamily: 'Courier', width: '50%', textAlign: 'right' }}>
        {value}
      </Text>
    </View>
  )

  return (
    <BigModal open={!!open} onClose={() => onClose()} style={{ height: 600 }}>
      <Box minHeight="80vh" flexGrow={1} flex={1} height="100%">
        <PDFViewer width="100%" height="100%">
          <Document
            title={invoice?.reference || 'Invoice'}
            author={orgSettings?.orgName || 'AmbuNet'}
          >
            <Page
              size="A4"
              style={{
                paddingTop: 29.7,
                paddingBottom: 29.7,
                paddingLeft: 40.0,
                paddingRight: 40.0,
                fontFamily: 'Helvetica',
              }}
            >
              <View
                style={{ flexDirection: 'column', gap: 2, paddingBottom: 10 }}
                wrap={true}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    position: 'center',
                    marginTop: 10,
                    left: 0,
                    right: 0,
                    width: '100%',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'column',
                      flex: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: 20,
                        fontWeight: 'bold',
                        color: '#1F3D7C',
                        marginBottom: 5,
                      }}
                    >
                      {orgSettings?.orgName || ''}
                    </Text>

                    <Text style={{ fontSize: 12 }}>
                      {orgAddress.line1 || ''}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {orgAddress.line2 || ''}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {orgAddress.town || ''}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {orgAddress.county || ''}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {orgAddress.postCode || ''}
                    </Text>
                    {orgSettings?.companyRegistrationNumber && (
                      <Text style={{ fontSize: 10, marginTop: 4 }}>
                        Company No. {orgSettings.companyRegistrationNumber}
                      </Text>
                    )}
                    {orgSettings?.vatNumber && (
                      <Text style={{ fontSize: 10 }}>
                        VAT No. {orgSettings.vatNumber}
                      </Text>
                    )}
                  </View>
                  <View
                    style={{
                      flexDirection: 'column',
                      width: '50%',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 24,
                        color: '#3c4e50',
                        fontFamily: 'Courier-Bold',
                        marginBottom: 5,
                      }}
                    >
                      INVOICE
                    </Text>
                    {orgSettings.logo && (
                      <Image
                        src={orgSettings.logo}
                        style={{
                          height: 30,
                          width: 'auto',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    <SmallText
                      title="Date"
                      value={moment(invoice.invoiceDate).format('Do MMM YYYY')}
                    />
                    <SmallText
                      title="Due"
                      value={moment(invoice.dueDate).format('Do MMM YYYY')}
                    />
                    <SmallText title="Reference" value={invoice?.reference || ''} />
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginTop: 14,
                    fontSize: 12,
                    fontFamily: 'Courier',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 2,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: 'Courier-Bold',
                      }}
                    >
                      To:
                    </Text>
                    <Text>{billToName}</Text>
                  </View>
                  {billToAddress
                    .filter(Boolean)
                    .map((x, index) => (
                      <Text key={index}>{x}</Text>
                    ))}
                </View>
                <View
                  style={{
                    flexDirection: 'column',
                    marginTop: 20,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      marginBottom: 10,
                    }}
                  >
                    Invoice Items
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      borderBottom: '1px solid #000',
                      paddingBottom: 5,
                      fontFamily: 'Helvetica-Bold',
                      fontSize: 12,
                    }}
                  >
                    <Text style={{ width: '40%' }}>Description</Text>
                    <Text style={{ width: '12%', textAlign: 'center' }}>
                      QTY
                    </Text>
                    <Text style={{ width: '12%', textAlign: 'right' }}>
                      Price
                    </Text>
                    <Text style={{ width: '12%', textAlign: 'right' }}>
                      Subtotal
                    </Text>
                    <Text style={{ width: '12%', textAlign: 'right' }}>
                      VAT
                    </Text>
                    <Text style={{ width: '12%', textAlign: 'right' }}>
                      Total
                    </Text>
                  </View>
                  {(invoice.items || []).map((item, index) => (
                    <View
                      key={index}
                      style={{
                        flexDirection: 'row',
                        borderBottom: '1px solid #e0e0e0',
                        paddingVertical: 5,
                        fontSize: 10,
                        fontWeight: 'light',
                      }}
                    >
                      <Text style={{ width: '40%' }}>{item.description}</Text>
                      <Text style={{ width: '12%', textAlign: 'center' }}>
                        {item.quantity}
                      </Text>
                      <Text style={{ width: '12%', textAlign: 'right' }}>
                        {currency}
                        {item.unitPrice}
                      </Text>
                      <Text style={{ width: '12%', textAlign: 'right' }}>
                        {currency}
                        {item.unitPrice * item.quantity}
                      </Text>
                      <Text style={{ width: '12%', textAlign: 'right' }}>
                        {currency}
                        {(
                          item.quantity *
                          item.unitPrice *
                          (vatMultiplierForItem(item.vat, region) - 1)
                        ).toFixed(2)}
                      </Text>
                      <Text style={{ width: '12%', textAlign: 'right' }}>
                        {(item.quantity ?? 0) * (item.unitPrice ?? 0) > 0 &&
                          `${currency}${parseFloat(
                            (item.quantity ?? 0) *
                              (item.unitPrice ?? 0) *
                              vatMultiplierForItem(item.vat, region),
                          ).toFixed(2)}`}
                      </Text>
                    </View>
                  ))}
                </View>
                {(invoice.payments || []).length > 0 && (
                  <View
                    style={{
                      flexDirection: 'column',
                      width: '40%',
                      fontSize: 14,
                    }}
                  >
                    <Text style={{ marginTop: 20, marginBottom: 10 }}>
                      Payments
                    </Text>
                    <View
                      style={{
                        flexDirection: 'row',
                        borderBottom: '1px solid #000',
                        paddingBottom: 5,
                        fontFamily: 'Helvetica-Bold',
                        fontSize: 12,
                      }}
                    >
                      <Text style={{ width: '70%' }}>Date</Text>
                      <Text style={{ width: '30%', textAlign: 'center' }}>
                        Amount
                      </Text>
                    </View>
                    {(invoice.payments || []).map((payment, index) => (
                      <View
                        key={index}
                        style={{
                          flexDirection: 'row',
                          borderBottom: '1px solid #e0e0e0',
                          paddingVertical: 2,
                          fontSize: 10,
                          fontWeight: 'light',
                        }}
                      >
                        <Text style={{ width: '70%' }}>
                          {moment(payment.date).format('Do MMM YYYY')}
                        </Text>
                        <Text style={{ width: '30%', textAlign: 'center' }}>
                          {currency}
                          {parseFloat(payment?.amount ?? 0).toFixed(2)}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
                <View
                  style={{
                    flexDirection: 'column',
                    fontSize: 14,
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      paddingBottom: 5,
                      fontSize: 12,
                      width: '40%',
                    }}
                  >
                    <Text
                      style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                    >
                      Subtotal
                    </Text>
                    <Text style={{ width: '30%', textAlign: 'right' }}>
                      {currency}
                      {_subtotal}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      paddingBottom: 5,
                      fontSize: 12,
                      width: '40%',
                    }}
                  >
                    <Text
                      style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                    >
                      VAT
                    </Text>
                    <Text style={{ width: '30%', textAlign: 'right' }}>
                      {currency}
                      {_vatTotal}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      paddingBottom: 5,
                      fontSize: 12,
                      width: '40%',
                    }}
                  >
                    <Text
                      style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                    >
                      Total
                    </Text>
                    <Text style={{ width: '30%', textAlign: 'right' }}>
                      {currency}
                      {(parseFloat(_subtotal) + parseFloat(_vatTotal)).toFixed(
                        2,
                      )}
                    </Text>
                  </View>
                  {invoice.discount > 0 && (
                    <View
                      style={{
                        flexDirection: 'row',
                        paddingBottom: 5,
                        fontSize: 12,
                        width: '40%',
                      }}
                    >
                      <Text
                        style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                      >
                        Discount
                      </Text>
                      <Text style={{ width: '30%', textAlign: 'right' }}>
                        -{currency}
                        {parseFloat(invoice.discount).toFixed(2)}
                      </Text>
                    </View>
                  )}
                  {parseFloat(_creditedTotal) > 0 && (
                    <View
                      style={{
                        flexDirection: 'row',
                        paddingBottom: 5,
                        fontSize: 12,
                        width: '40%',
                      }}
                    >
                      <Text
                        style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                      >
                        Credited
                      </Text>
                      <Text style={{ width: '30%', textAlign: 'right' }}>
                        -{currency}
                        {_creditedTotal}
                      </Text>
                    </View>
                  )}
                  <View
                    style={{
                      flexDirection: 'row',
                      paddingBottom: 5,
                      fontSize: 12,
                      width: '40%',
                    }}
                  >
                    <Text
                      style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                    >
                      Payments Made
                    </Text>
                    <Text style={{ width: '30%', textAlign: 'right' }}>
                      {currency}
                      {_paymentsTotal}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      paddingBottom: 5,
                      fontSize: 12,
                      width: '40%',
                    }}
                  >
                    <Text
                      style={{ width: '70%', fontFamily: 'Helvetica-Bold' }}
                    >
                      Balance Due
                    </Text>
                    <Text style={{ width: '30%', textAlign: 'right' }}>
                      {currency}
                      {(
                        parseFloat(_subtotal) +
                        parseFloat(_vatTotal) -
                        parseFloat(invoice.discount || 0) -
                        parseFloat(_creditedTotal) -
                        parseFloat(_paymentsTotal)
                      ).toFixed(2)}
                    </Text>
                  </View>
                </View>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      </Box>
    </BigModal>
  )
}

export default PDFInvoice
